import { Injectable } from '@angular/core';
import { ETicketSearchParts, } from "app_sasha/shared/services/search-form-helper/search-form-helper.service";
import {
    ITicketSearchFormBookingOutput,
    ITicketSearchFormBookingOutputCustomField
} from "./ticketing-booking-fieldset/ticketing-booking-fieldset.component";
import { ITicketSearchFormActionOutput } from "app_sasha/pages/ticketing-report/components/ticketing-search-form/ticketing-action-fieldset/ticketing-action-fieldset.component";
import { ITicketSearchFormTicketOutput } from "app_sasha/pages/ticketing-report/components/ticketing-search-form/ticketing-ticket-fieldset/ticketing-ticket-fieldset.component";
import {
    RequestForTicketingReportSearch, TicketingReportSearchAction,
    TicketingReportSearchBooking, TicketingReportSearchBookingCustomField,
    TicketingReportSearchTicket
} from "@afidium/ts-facade";
import * as moment from "moment";
import { DateService } from "libraries/date/date.service";

export interface ITicketSearchFormOutput {
    [ETicketSearchParts.freeSearch]: string[];
    [ETicketSearchParts.action]: ITicketSearchFormActionOutput;
    [ETicketSearchParts.booking]: ITicketSearchFormBookingOutput;
    [ETicketSearchParts.ticket]: ITicketSearchFormTicketOutput;
}

@Injectable()
export class TicketSearchFormMapperService {

    constructor(
        private readonly dateService: DateService,
    ) {
    }

    public mapFormOutputToRequest(formOutput: ITicketSearchFormOutput): RequestForTicketingReportSearch {
        return this.removeEmpty({
            freeSearchArray: formOutput.freeSearch,
            action: this.mapAction(formOutput[ETicketSearchParts.action]),
            booking: this.mapBooking(formOutput[ETicketSearchParts.booking]),
            ticket: this.mapTicket(formOutput[ETicketSearchParts.ticket]),
        });
    }

    public mapApiActionToFormAction(apiAction: TicketingReportSearchAction): ITicketSearchFormActionOutput {
        return {
            types: apiAction.typeArray || [],
            event: {
                eventMin: apiAction.event?.eventMin ? this.dateService.getStr2JsDate(apiAction.event.eventMin) : null,
                eventMax: apiAction.event?.eventMax ? this.dateService.getStr2JsDate(apiAction.event.eventMax) : null,
            },
            author: apiAction.authorArray || [],
        };
    }

    public mapApiTicketToFormTicket(apiTicket: TicketingReportSearchTicket): ITicketSearchFormTicketOutput {
        return {
            system: apiTicket.systemArray || [],
            vendors: apiTicket.vendorArray || [],
            types: apiTicket.typeArray || [],
            numbers: apiTicket.numberArray || [],
            agencies: apiTicket.agencyArray || [],
            agents: apiTicket.agentArray || [],
            travellers: apiTicket.travellerArray || [],
            amount: {
                amountMin: this.valueToAmmount(apiTicket.amount?.amountMin),
                amountMax: this.valueToAmmount(apiTicket.amount?.amountMax),
            },
            commission: {
                commissionMin: this.valueToAmmount(apiTicket.commission?.commissionMin),
                commissionMax: this.valueToAmmount(apiTicket.commission?.commissionMax),
            },
            emission: {
                emissionMin: apiTicket.emission?.emissionMin ? this.dateService.getStr2JsDate(apiTicket?.emission.emissionMin) : null,
                emissionMax: apiTicket.emission?.emissionMax ? this.dateService.getStr2JsDate(apiTicket?.emission.emissionMax) : null,
            },
            payment: apiTicket.paymentArray || [],
        };
    }

    public mapApiBookingToFormBooking(apiBooking: TicketingReportSearchBooking): ITicketSearchFormBookingOutput {
        return {
            references: apiBooking.referenceArray || [],
            creation: {
                creationMin: apiBooking.creation?.creationMin ? this.dateService.getStr2JsDate(apiBooking.creation.creationMin) : null,
                creationMax: apiBooking.creation?.creationMax ? this.dateService.getStr2JsDate(apiBooking.creation.creationMax) : null,
            },
            begin: {
                beginMin: apiBooking.begin?.beginMin ? this.dateService.getStr2JsDate(apiBooking.begin.beginMin) : null,
                beginMax: apiBooking.begin?.beginMax ? this.dateService.getStr2JsDate(apiBooking.begin.beginMax) : null,
            },
            customFields: apiBooking.customFieldArray?.length ?
                apiBooking.customFieldArray
                    .map((aipCustomField: TicketingReportSearchBookingCustomField): ITicketSearchFormBookingOutputCustomField => {
                        return {code: aipCustomField.name, value: aipCustomField.value};
                    }) :
                [],
        };
    }

    private valueToAmmount(value: number): number {
        if (value === 0) {
            return 0;
        }
        if (!value) {
            return null;
        }
        return value / 100;
    }

    private mapTicket(formTicket: ITicketSearchFormTicketOutput): TicketingReportSearchTicket {
        return {
            systemArray: formTicket.system,
            typeArray: formTicket.types,
            numberArray: formTicket.numbers,
            agencyArray: formTicket.agencies,
            agentArray: formTicket.agents,
            travellerArray: formTicket.travellers,
            amount: {
                amountMin: this.amountToValue(formTicket.amount?.amountMin),
                amountMax: this.amountToValue(formTicket.amount?.amountMax),
            },
            commission: {
                commissionMin: this.amountToValue(formTicket.commission?.commissionMin),
                commissionMax: this.amountToValue(formTicket.commission?.commissionMax),
            },
            emission: {
                emissionMin: this.dateToApiString(formTicket.emission?.emissionMin),
                emissionMax: this.dateToApiString(formTicket.emission?.emissionMax),
            },
            paymentArray: formTicket.payment,
            vendorArray: formTicket.vendors,
        };
    }

    private amountToValue(value: number): number {
        if (value === 0) {
            return 0;
        }
        return value ? value * 100 : null;
    }

    private dateToApiString(date: Date): string {
        return date ? moment(date).format('YYYY-MM-DD') : null;
    }

    private mapBooking(formBooking: ITicketSearchFormBookingOutput): TicketingReportSearchBooking {
        return {
            referenceArray: formBooking.references,
            creation: formBooking.creation ? {
                creationMin: this.dateToApiString(formBooking.creation.creationMin),
                creationMax: this.dateToApiString(formBooking.creation.creationMax),
            } : null,
            begin: formBooking.begin ? {
                beginMin: this.dateToApiString(formBooking.begin.beginMin),
                beginMax: this.dateToApiString(formBooking.begin.beginMax),
            } : null,
            customFieldArray: formBooking.customFields?.length ?
                formBooking.customFields
                    .filter((fromCustomField: ITicketSearchFormBookingOutputCustomField): boolean => Boolean(fromCustomField.value))
                    .map((formCustomField: ITicketSearchFormBookingOutputCustomField): TicketingReportSearchBookingCustomField =>
                        ({value: formCustomField.value, name: formCustomField.code})) :
                null,
        };
    }

    private mapAction(formAction: ITicketSearchFormActionOutput): TicketingReportSearchAction {
        return {
            typeArray: formAction.types,
            event: formAction.event ? {
                eventMin: this.dateToApiString(formAction.event.eventMin),
                eventMax: this.dateToApiString(formAction.event.eventMax),
            } : null,
            authorArray: formAction.author
        };
    }

    private removeEmpty(object: RequestForTicketingReportSearch): RequestForTicketingReportSearch {
        Object.keys(object).forEach((key: string): void => {
            // If Object recall function, keep in mind : Array is Object in JS
            if (object[key] && typeof object[key] === 'object') {
                // Recursion
                // @ts-ignore
                this.removeEmpty(object[key]);

                // If object is empty then delete it
                if (!Object.keys(object[key]).length) {
                    // tslint:disable-next-line:no-dynamic-delete
                    delete object[key];
                }
            }
            // If null delete property
            else if (object[key] === null || object[key] === '' || object[key] === ' ') {
                // tslint:disable-next-line:no-dynamic-delete
                delete object[key];
            }
        });
        return object;
    }
}
